// Shared premium game FX: bokeh background, announcement banner, scoring tiles, dark frame wrapper.
// Used by Spin Wheel / Memory Match / Flashcard / Quiz Battle / Scratch Card to give them the
// same Ludo / Snake & Ladder "casino felt" premium feel.
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Soft floating coloured orbs behind a dark felt — cheap on mobile, pure CSS.
export function AmbientBokeh({ orbs }) {
    const defaults = [
        { color: '#FFF200', x: '10%', y: '20%', size: 220, delay: 0 },
        { color: '#E31E24', x: '78%', y: '18%', size: 200, delay: 1.5 },
        { color: '#10b981', x: '15%', y: '78%', size: 220, delay: 3 },
        { color: '#a855f7', x: '82%', y: '76%', size: 220, delay: 4.5 },
    ];
    const list = orbs || defaults;
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {list.map((o, i) => (
                <motion.div key={i}
                    initial={{ opacity: 0.25, scale: 0.8 }}
                    animate={{
                        opacity: [0.18, 0.32, 0.18],
                        scale: [0.85, 1.05, 0.85],
                        x: [0, 30, -20, 0],
                        y: [0, -20, 25, 0],
                    }}
                    transition={{ duration: 9 + i * 1.2, repeat: Infinity, ease: 'easeInOut', delay: o.delay }}
                    className="absolute rounded-full"
                    style={{
                        left: o.x, top: o.y, width: o.size, height: o.size,
                        transform: 'translate(-50%, -50%)',
                        background: `radial-gradient(circle at center, ${o.color}66 0%, ${o.color}11 50%, transparent 75%)`,
                        filter: 'blur(28px)',
                    }} />
            ))}
            <div className="absolute inset-0 opacity-[0.06]"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 6px)' }} />
        </div>
    );
}

// Premium dark frame wrapper that hosts the game. Auto-includes the bokeh background.
export function GameFrame({ children, orbs, dataTestId, className = '' }) {
    return (
        <div className={`relative bg-gradient-to-br from-slate-900 via-slate-950 to-black rounded-3xl p-3 sm:p-5 shadow-2xl ring-1 ring-white/10 overflow-hidden ${className}`}
            data-testid={dataTestId}>
            <AmbientBokeh orbs={orbs} />
            <div className="relative z-10">{children}</div>
        </div>
    );
}

// Big celebratory announcement banner ("CORRECT!", "+50 pts", "JACKPOT!").
export function AnnouncementBanner({ announce }) {
    if (!announce) return null;
    const toneStyles = {
        gold:  { from: '#FFF200', to: '#E0A800', text: '#231F20', shadow: '0 0 30px rgba(255,242,0,0.8)' },
        red:   { from: '#E31E24', to: '#7F1010', text: '#FFFFFF', shadow: '0 0 30px rgba(227,30,36,0.8)' },
        green: { from: '#10B981', to: '#065F46', text: '#FFFFFF', shadow: '0 0 30px rgba(16,185,129,0.8)' },
        blue:  { from: '#3B82F6', to: '#1E3A8A', text: '#FFFFFF', shadow: '0 0 30px rgba(59,130,246,0.8)' },
    };
    const t = toneStyles[announce.tone] || toneStyles.gold;
    return (
        <AnimatePresence>
            <motion.div
                key={announce.id}
                initial={{ y: -80, scale: 0.5, opacity: 0, rotate: -8 }}
                animate={{ y: 0, scale: [0.5, 1.25, 1], opacity: 1, rotate: [-8, 4, 0] }}
                exit={{ y: -40, scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut', times: [0, 0.6, 1] }}
                className="absolute top-1/4 left-1/2 -translate-x-1/2 pointer-events-none z-40"
            >
                <div
                    className="px-6 py-3 rounded-2xl font-display font-extrabold text-2xl sm:text-3xl tracking-wide whitespace-nowrap"
                    style={{
                        background: `linear-gradient(180deg, ${t.from} 0%, ${t.to} 100%)`,
                        color: t.text,
                        boxShadow: t.shadow + ', 0 8px 24px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.25)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        border: '2px solid rgba(0,0,0,0.5)',
                    }}
                >
                    {announce.text}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

// Gradient score tiles (label above, big extruded value).
export function ScoreTile({ label, value, accent = 'from-amber-400 to-yellow-500' }) {
    return (
        <div className={`rounded-2xl bg-gradient-to-br ${accent} p-2 sm:p-2.5 text-slate-900 shadow text-center min-w-[3.5rem]`}>
            <div className="text-[9px] uppercase tracking-wide font-bold opacity-90">{label}</div>
            <div className="font-display font-extrabold text-base sm:text-lg leading-none">{value}</div>
        </div>
    );
}

// Sound utility — short tone using WebAudio. Use sparingly.
export function playGameSound(kind, muted = false) {
    if (muted) return;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        const map = { roll: 280, click: 540, correct: 880, wrong: 180, win: 1320, jackpot: 1500, flip: 700, match: 1000 };
        o.type = kind === 'win' || kind === 'jackpot' ? 'triangle' : kind === 'wrong' ? 'sawtooth' : 'sine';
        o.frequency.value = map[kind] || 440;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o.start(); o.stop(ctx.currentTime + 0.36);
    } catch { /* best effort */ }
}

// Premium gold pill button used across all games.
export function GoldButton({ children, onClick, disabled, dataTestId, className = '' }) {
    return (
        <button onClick={onClick} disabled={disabled}
            data-testid={dataTestId}
            className={`px-5 sm:px-6 py-3 rounded-2xl bg-gradient-to-r from-brand-yellow to-amber-400 text-brand-black font-display font-extrabold uppercase tracking-wider text-sm sm:text-base shadow-lg hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 ${className}`}
            style={{ boxShadow: '0 8px 24px rgba(255,242,0,0.45), inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.25)' }}>
            {children}
        </button>
    );
}
